/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { LuDownload, LuGrip } from 'react-icons/lu'

export interface ListControlsProps {
  /** Omit on pages without a map, or in layouts without one. */
  limitToMap?: { isOn: boolean; onToggle: () => void; control?: 'button' | 'toggle' }
  onDownload: () => void
  /** Omit on pages without a column chooser. */
  onConfigure?: () => void
  /** Nothing to act on yet, e.g. no company chosen. */
  disabled?: boolean
}

/**
 * Limit to map, export and column chooser for a list page, at the start of its toolbar's second
 * row (the toolbar wraps them to the line just above the list when space runs short).
 */
export const ListControls = ({ limitToMap, onDownload, onConfigure, disabled = false }: ListControlsProps) => {
  const iconButton = `btn btn-ghost btn-sm p-1 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer text-primary'}`
  const limitToMapTitle = limitToMap?.isOn
    ? 'Showing only items in the map viewport'
    : 'Showing everything that matches'

  return (
    <div className="flex shrink-0 items-center gap-2">
      {limitToMap?.control === 'toggle' ? (
        <label className="flex shrink-0 cursor-pointer items-center gap-2" title={limitToMapTitle}>
          <input
            type="checkbox"
            className="toggle toggle-sm toggle-primary"
            checked={limitToMap.isOn}
            onChange={limitToMap.onToggle}
            aria-label="Show only items in the map viewport"
          />
        </label>
      ) : (
        limitToMap && (
          <button
            onClick={limitToMap.onToggle}
            className={`btn btn-sm shrink-0 ${limitToMap.isOn ? 'btn-primary' : 'btn-ghost border border-base-300'}`}
            title={limitToMapTitle}
          >
            Limit to map
          </button>
        )
      )}

      <button className={iconButton} disabled={disabled} onClick={onDownload} title="Export to Excel">
        <LuDownload className="text-xl" />
      </button>

      {onConfigure && (
        <button className={iconButton} disabled={disabled} onClick={onConfigure} title="Customize Columns">
          <LuGrip className="text-xl" />
        </button>
      )}
    </div>
  )
}
